/*
  LIME LABS COMMERCE LAYER
  Component: Collection Related Rail
  Project: La Troupe
  Version: 2.1
  Status: LLCL-QA

  Progressive enhancement only.
  Native horizontal scrolling and links remain functional without JS.

  v2.1
  - Pointer capture only begins after a real drag is detected.
  - Normal card clicks remain native and reliable.
  - Dragging suppresses accidental link activation.
  - Touch / trackpad native horizontal scrolling remains untouched.
*/

(() => {
  'use strict';

  const ROOT = '[data-ll-related-rail]';
  const VIEWPORT = '[data-ll-related-viewport]';
  const CARD = '[data-ll-related-card]';
  const PREV = '[data-ll-related-prev]';
  const NEXT = '[data-ll-related-next]';

  const DRAG_THRESHOLD = 6;

  const reducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  );

  const bind = (root) => {
    if (!root) return;
    if (root.dataset.llRelatedRailBound === 'true') return;

    const viewport = root.querySelector(VIEWPORT);
    const cards = Array.from(root.querySelectorAll(CARD));
    const prev = root.querySelector(PREV);
    const next = root.querySelector(NEXT);

    if (!viewport || cards.length < 2) return;

    root.dataset.llRelatedRailBound = 'true';

    /*
     * ------------------------------------------------------------
     * Scroll state
     * ------------------------------------------------------------
     */

    const maxScroll = () => {
      return Math.max(
        0,
        viewport.scrollWidth - viewport.clientWidth
      );
    };

    const updateControls = () => {
      const max = maxScroll();
      const current = viewport.scrollLeft;

      root.classList.toggle('is-static', max <= 2);

      if (prev) {
        prev.disabled = current <= 2;
      }

      if (next) {
        next.disabled = current >= max - 2;
      }
    };

    /*
     * ------------------------------------------------------------
     * Card step
     * ------------------------------------------------------------
     */

    const cardStep = () => {
      if (cards.length > 1) {
        const distance =
          cards[1].offsetLeft - cards[0].offsetLeft;

        if (distance > 0) {
          return distance;
        }
      }

      return (
        cards[0]?.offsetWidth ||
        viewport.clientWidth * 0.6
      );
    };

    /*
     * ------------------------------------------------------------
     * Arrow navigation
     * ------------------------------------------------------------
     */

    const move = (direction) => {
      viewport.scrollBy({
        left: cardStep() * direction,
        behavior: reducedMotion.matches
          ? 'auto'
          : 'smooth'
      });
    };

    prev?.addEventListener('click', () => {
      move(-1);
    });

    next?.addEventListener('click', () => {
      move(1);
    });

    /*
     * ------------------------------------------------------------
     * Scroll observer
     * ------------------------------------------------------------
     */

    viewport.addEventListener(
      'scroll',
      updateControls,
      { passive: true }
    );

    /*
     * ------------------------------------------------------------
     * Keyboard navigation
     * ------------------------------------------------------------
     */

    viewport.addEventListener('keydown', (event) => {
      /*
       * Do not intercept keyboard events originating
       * from cards, links or controls inside the viewport.
       */
      if (event.target !== viewport) return;

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        move(1);
        return;
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        move(-1);
      }
    });

    /*
     * ------------------------------------------------------------
     * Mouse drag scrolling
     * ------------------------------------------------------------
     *
     * Important:
     * Pointer capture MUST NOT happen on pointerdown.
     *
     * A normal click on a collection card must remain a
     * completely native browser click.
     *
     * Pointer capture begins only once horizontal movement
     * passes DRAG_THRESHOLD.
     */

    let pointerId = null;
    let startX = 0;
    let startScroll = 0;

    let dragging = false;
    let moved = false;
    let suppressClick = false;

    const releasePointer = () => {
      if (pointerId === null) return;

      if (
        viewport.hasPointerCapture?.(pointerId)
      ) {
        try {
          viewport.releasePointerCapture(pointerId);
        } catch (error) {
          /*
           * Pointer may already have been released by browser.
           * No action required.
           */
        }
      }
    };

    const resetPointerState = () => {
      root.classList.remove('is-dragging');

      pointerId = null;
      startX = 0;
      startScroll = 0;
      dragging = false;
      moved = false;
    };

    const endDrag = (event) => {
      if (pointerId === null) return;

      if (
        event &&
        typeof event.pointerId !== 'undefined' &&
        event.pointerId !== pointerId
      ) {
        return;
      }

      const didDrag = moved;

      releasePointer();

      root.classList.remove('is-dragging');

      pointerId = null;
      dragging = false;
      moved = false;

      /*
       * Prevent the browser-generated click after a drag.
       * A normal click never reaches this state.
       */
      if (didDrag) {
        suppressClick = true;

        window.setTimeout(() => {
          suppressClick = false;
        }, 0);
      }
    };

    viewport.addEventListener(
      'pointerdown',
      (event) => {
        /*
         * Native touch and pen scrolling remain untouched.
         */
        if (event.pointerType !== 'mouse') return;

        /*
         * Only primary mouse button.
         */
        if (event.button !== 0) return;

        pointerId = event.pointerId;
        startX = event.clientX;
        startScroll = viewport.scrollLeft;

        dragging = false;
        moved = false;
        suppressClick = false;
      }
    );

    viewport.addEventListener(
      'pointermove',
      (event) => {
        if (pointerId === null) return;
        if (event.pointerId !== pointerId) return;

        const delta =
          event.clientX - startX;

        /*
         * Still a potential normal click.
         */
        if (
          !dragging &&
          Math.abs(delta) <= DRAG_THRESHOLD
        ) {
          return;
        }

        /*
         * Real drag detected.
         *
         * Pointer capture starts HERE,
         * not on pointerdown.
         */
        if (!dragging) {
          dragging = true;
          moved = true;

          root.classList.add('is-dragging');

          try {
            viewport.setPointerCapture?.(
              pointerId
            );
          } catch (error) {
            /*
             * Drag still works even if capture is unavailable.
             */
          }
        }

        if (!dragging) return;

        moved = true;

        viewport.scrollLeft =
          startScroll - delta;

        /*
         * Prevent browser text/image drag behavior only
         * after actual horizontal rail dragging begins.
         */
        event.preventDefault();
      }
    );

    viewport.addEventListener(
      'pointerup',
      endDrag
    );

    viewport.addEventListener(
      'pointercancel',
      endDrag
    );

    /*
     * If pointer capture is lost unexpectedly,
     * make sure visual drag state is cleared.
     */
    viewport.addEventListener(
      'lostpointercapture',
      () => {
        if (!dragging) return;

        root.classList.remove('is-dragging');
        dragging = false;
      }
    );

    /*
     * If mouse exits before an actual drag starts,
     * abandon the pending interaction.
     *
     * Once dragging starts, pointer capture handles
     * movement outside the viewport.
     */
    viewport.addEventListener(
      'pointerleave',
      (event) => {
        if (pointerId === null) return;
        if (event.pointerId !== pointerId) return;
        if (dragging) return;

        resetPointerState();
      }
    );

    /*
     * ------------------------------------------------------------
     * Accidental click suppression after drag
     * ------------------------------------------------------------
     */

    viewport.addEventListener(
      'click',
      (event) => {
        if (!suppressClick) return;

        event.preventDefault();
        event.stopPropagation();

        /*
         * stopImmediatePropagation is intentional here:
         * after a drag we don't want another theme listener
         * interpreting the synthetic click as navigation.
         */
        event.stopImmediatePropagation?.();

        suppressClick = false;
      },
      true
    );

    /*
     * ------------------------------------------------------------
     * Prevent native image drag while rail is being dragged
     * ------------------------------------------------------------
     */

    viewport.addEventListener(
      'dragstart',
      (event) => {
        if (!dragging) return;

        event.preventDefault();
      }
    );

    /*
     * ------------------------------------------------------------
     * Resize handling
     * ------------------------------------------------------------
     */

    if ('ResizeObserver' in window) {
      const observer =
        new ResizeObserver(() => {
          updateControls();
        });

      observer.observe(viewport);

      cards.forEach((card) => {
        observer.observe(card);
      });
    } else {
      window.addEventListener(
        'resize',
        updateControls,
        { passive: true }
      );
    }

    /*
     * ------------------------------------------------------------
     * Initial state
     * ------------------------------------------------------------
     */

    requestAnimationFrame(
      updateControls
    );
  };

  /*
   * ------------------------------------------------------------
   * Boot
   * ------------------------------------------------------------
   */

  const boot = (scope = document) => {
    if (!scope) return;

    if (scope.matches?.(ROOT)) {
      bind(scope);
    }

    scope
      .querySelectorAll?.(ROOT)
      .forEach(bind);
  };

  boot();

  /*
   * Shopify Theme Editor support.
   */
  document.addEventListener(
    'shopify:section:load',
    (event) => {
      boot(event.target);
    }
  );
})();